import { Button, Input, message, Space } from 'antd'
import Checkmark from 'assets/ca-checkmark.svg?react'
import XCross from 'assets/ca-x-cross.svg?react'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'

const fadeInMini = keyframes`
  from {
    opacity: 0;
    transform: translateY(0.625rem);
  }
  to {
    opacity: 0.95;
    transform: translateY(0);
  }
`

const ControlsContainer = styled.div`
  position: absolute;
  top: 3.75rem;
  right: 1.25rem;
  background: rgba(0, 0, 0, 0.6);
  padding: 0.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 14rem;
  width: 16rem;
  animation: ${fadeInMini} 0.3s ease-out forwards;
  opacity: 0.95;
  transition: opacity 0.2s ease;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: default;
`

const ControlLabel = styled.div`
  font-size: 0.875rem;
  opacity: 0.95;
  letter-spacing: 0.05rem;
  margin-bottom: 0.25rem;
`

const StyledSpace = styled(Space)`
  width: 100%;
`

const StyledButtonRow = styled(Space)`
  width: 100%;
  display: flex;
  gap: 0.5rem;
`

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const StatusIcon = styled.div<{ $visible: boolean }>`
  width: 24px;
  height: 24px;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  transition: opacity 0.3s ease-out;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
  }
`

const StatusContainer = styled.div`
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`

enum TextSegmentationStatus {
  Loading = 'loading',
  Success = 'success',
  Error = 'error',
}

export const TextSegmentationTools: React.FC = () => {
  const { sendCommand, onTextSegmentationComplete } = useKestrelViewerContext()
  const [textValue, setTextValue] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [status, setStatus] = useState<TextSegmentationStatus | null>(null)
  const requestStartTimeRef = useRef<number | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const statusTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isLoadingRef = useRef(false)

  // Update ref when isLoading changes
  useEffect(() => {
    isLoadingRef.current = isLoading
  }, [isLoading])

  // Effect to automatically clear status after 3 seconds when it changes to success or error
  useEffect(() => {
    // Clear any existing status timeout
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current)
      statusTimeoutRef.current = null
    }

    // If status is success or error, set a timeout to clear it after 3 seconds
    if (
      status === TextSegmentationStatus.Success ||
      status === TextSegmentationStatus.Error
    ) {
      statusTimeoutRef.current = setTimeout(() => {
        setStatus(null)
        statusTimeoutRef.current = null
      }, 3000)
    }

    // Cleanup on unmount or when status changes
    return () => {
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current)
        statusTimeoutRef.current = null
      }
    }
  }, [status])

  // Set up callback to be notified when textSegmentation batch is received
  useEffect(() => {
    if (!onTextSegmentationComplete) return

    const handleTextSegmentationComplete = () => {
      // Only handle if we're currently waiting for a response
      if (isLoadingRef.current && requestStartTimeRef.current) {
        // Success! Clear the 10-second timeout and show checkmark
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
          timeoutRef.current = null
        }
        setIsLoading(false)
        setStatus(TextSegmentationStatus.Success)
        // The status effect will handle clearing it after 3 seconds
      }
    }

    // Register the callback
    onTextSegmentationComplete(handleTextSegmentationComplete)

    // Cleanup: unregister callback on unmount
    return () => {
      onTextSegmentationComplete(() => {})
    }
  }, [onTextSegmentationComplete])

  // Handle 10-second timeout
  useEffect(() => {
    if (isLoading && requestStartTimeRef.current) {
      const startTime = requestStartTimeRef.current
      timeoutRef.current = setTimeout(() => {
        // Only show error if we're still waiting for this specific request
        if (requestStartTimeRef.current === startTime) {
          setIsLoading(false)
          setStatus(TextSegmentationStatus.Error)
          // The status effect will handle clearing it after 3 seconds
        }
      }, 10000)
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [isLoading])

  const handleApply = useCallback(() => {
    const trimmedText = textValue.trim()
    if (!trimmedText) {
      message.error('Input a text prompt')
      return
    }
    if (trimmedText.length > 50) {
      message.error('Input must be 50 characters or less')
      return
    }

    // Clear any existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current)
      statusTimeoutRef.current = null
    }

    // Reset status and start loading state
    setStatus(null)
    setIsLoading(true)
    setStatus(TextSegmentationStatus.Loading)
    requestStartTimeRef.current = Date.now()

    sendCommand({
      command: KestrelCommands.APPLY_TEXT_SEGMENTATION,
      prompt: trimmedText,
    })
  }, [sendCommand, textValue])

  const handleClear = useCallback(() => {
    setTextValue('')
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      e.stopPropagation()
      if (e.key === 'Enter') {
        e.preventDefault()
        handleApply()
      }
    },
    [handleApply]
  )

  const stopPropagation = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation()
  }, [])

  return (
    <ControlsContainer onClick={stopPropagation} onMouseDown={stopPropagation}>
      <ControlLabel>Text Segmentation</ControlLabel>
      <StatusContainer>
        {status === TextSegmentationStatus.Loading && <LoadingSpinner />}
        {status === TextSegmentationStatus.Success && (
          <StatusIcon $visible={true}>
            <Checkmark style={{ color: '#52c41a' }} />
          </StatusIcon>
        )}
        {status === TextSegmentationStatus.Error && (
          <StatusIcon $visible={true}>
            <XCross style={{ color: '#ff4d4f' }} />
          </StatusIcon>
        )}
      </StatusContainer>
      <StyledSpace orientation='vertical' size='small'>
        <Input
          value={textValue}
          onChange={(e) => setTextValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onKeyUp={stopPropagation}
          onClick={stopPropagation}
          onMouseDown={stopPropagation}
          onMouseUp={stopPropagation}
          placeholder='Enter text...'
          style={{ width: '100%' }}
          maxLength={50}
          showCount
          autoFocus
          disabled={isLoading}
        />
        <StyledButtonRow>
          <Button
            type='primary'
            onClick={handleApply}
            style={{ flex: 1 }}
            disabled={isLoading}>
            Apply
          </Button>
          <Button
            onClick={handleClear}
            style={{ flex: 1 }}
            disabled={isLoading}>
            Clear
          </Button>
        </StyledButtonRow>
      </StyledSpace>
    </ControlsContainer>
  )
}
